/**
 * API Service pour les Worksheets
 */

import api from './api';
import {
  Workstation,
  Worksheet,
  WorksheetTask,
  WorksheetTemplate,
  WorksheetStats,
  CreateWorksheetDto,
  ValidateWorksheetDto,
  CompleteTaskDto,
  SkipTaskDto,
  AssignWorksheetDto,
  WorksheetStatus,
  WorksheetType,
  TaskDefinition,
  TriggerType,
} from '../types/worksheets.types';

// ============================================================================
// WORKSTATIONS
// ============================================================================

export async function getWorkstations(includeInactive = false): Promise<Workstation[]> {
  return api.get('/workstations', { params: { include_inactive: includeInactive } });
}

export async function getWorkstation(id: string): Promise<Workstation> {
  return api.get(`/workstations/${id}`);
}

export async function createWorkstation(data: {
  code: string;
  name: string;
  description?: string;
}): Promise<Workstation> {
  return api.post('/workstations', data);
}

export async function updateWorkstation(
  id: string,
  data: { name?: string; description?: string; is_active?: boolean }
): Promise<Workstation> {
  return api.patch(`/workstations/${id}`, data);
}

export async function deleteWorkstation(id: string): Promise<{ success: boolean; message: string }> {
  return api.delete(`/workstations/${id}`);
}

export async function seedWorkstations(): Promise<{ created: string[]; skipped: string[]; total: number }> {
  return api.post('/workstations/seed');
}

// ============================================================================
// WORKSHEETS
// ============================================================================

export async function getWorksheets(params?: {
  status?: WorksheetStatus;
  workstation_id?: string;
  type?: WorksheetType;
  from_date?: string;
  to_date?: string;
}): Promise<Worksheet[]> {
  return api.get('/worksheets', { params });
}

export async function getWorksheet(id: string): Promise<Worksheet> {
  return api.get(`/worksheets/${id}`);
}

export async function createWorksheet(data: CreateWorksheetDto): Promise<Worksheet> {
  return api.post('/worksheets', data);
}

export async function updateWorksheet(id: string, data: Partial<CreateWorksheetDto>): Promise<Worksheet> {
  return api.patch(`/worksheets/${id}`, data);
}

export async function deleteWorksheet(id: string): Promise<{ success: boolean }> {
  return api.delete(`/worksheets/${id}`);
}

// Workflow actions
export async function validateWorksheet(id: string, data: ValidateWorksheetDto): Promise<Worksheet> {
  return api.post(`/worksheets/${id}/validate`, data);
}

export async function assignWorksheet(id: string, data: AssignWorksheetDto): Promise<Worksheet> {
  return api.post(`/worksheets/${id}/assign`, data);
}

export async function startWorksheet(id: string): Promise<Worksheet> {
  return api.post(`/worksheets/${id}/start`);
}

export async function completeWorksheet(id: string): Promise<Worksheet> {
  return api.post(`/worksheets/${id}/complete`);
}

export async function cancelWorksheet(id: string): Promise<Worksheet> {
  return api.post(`/worksheets/${id}/cancel`);
}

// Stats
export async function getWorksheetStats(): Promise<WorksheetStats> {
  return api.get('/worksheets/stats');
}

// ============================================================================
// TASKS
// ============================================================================

export async function getWorksheetTasks(worksheetId: string): Promise<WorksheetTask[]> {
  return api.get(`/worksheets/${worksheetId}/tasks`);
}

export async function startTask(worksheetId: string, taskId: string): Promise<WorksheetTask> {
  return api.post(`/worksheets/${worksheetId}/tasks/${taskId}/start`);
}

export async function completeTask(
  worksheetId: string,
  taskId: string,
  data?: CompleteTaskDto
): Promise<{ task: WorksheetTask; next_task?: WorksheetTask; worksheet_completed: boolean }> {
  return api.post(`/worksheets/${worksheetId}/tasks/${taskId}/complete`, data || {});
}

export async function skipTask(
  worksheetId: string,
  taskId: string,
  data: SkipTaskDto
): Promise<{ task: WorksheetTask; next_task?: WorksheetTask; worksheet_completed: boolean }> {
  return api.post(`/worksheets/${worksheetId}/tasks/${taskId}/skip`, data);
}

// ============================================================================
// WORKSHEET TEMPLATES
// ============================================================================

export async function getWorksheetTemplates(params?: {
  workstation_id?: string;
  type?: WorksheetType;
  trigger_type?: TriggerType;
  active_only?: boolean;
}): Promise<WorksheetTemplate[]> {
  return api.get('/worksheet-templates', { params });
}

export async function getWorksheetTemplate(id: string): Promise<WorksheetTemplate> {
  return api.get(`/worksheet-templates/${id}`);
}

export async function createWorksheetTemplate(data: {
  workstation_id: string;
  type: WorksheetType;
  trigger_type: TriggerType;
  trigger_cron?: string;
  trigger_event?: string;
  name: string;
  description?: string;
  default_assignee_id?: string;
  task_definitions: TaskDefinition[];
}): Promise<WorksheetTemplate> {
  return api.post('/worksheet-templates', data);
}

export async function updateWorksheetTemplate(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    trigger_cron: string;
    trigger_event: string;
    default_assignee_id: string;
    task_definitions: TaskDefinition[];
    is_active: boolean;
  }>
): Promise<WorksheetTemplate> {
  return api.patch(`/worksheet-templates/${id}`, data);
}

export async function deleteWorksheetTemplate(id: string): Promise<{ success: boolean }> {
  return api.delete(`/worksheet-templates/${id}`);
}

export async function generateFromTemplate(templateId: string): Promise<Worksheet> {
  return api.post(`/worksheet-templates/${templateId}/generate`);
}

export async function seedDefaultTemplates(): Promise<{ created: string[]; skipped: string[]; total: number }> {
  return api.post('/worksheet-templates/seed');
}

// ============================================================================
// EXECUTOR (for iPad interface)
// ============================================================================

export async function getAssignedWorksheets(vendorId: string): Promise<Worksheet[]> {
  return api.get('/worksheets', { params: { assigned_to_id: vendorId, status: 'TODO,IN_PROGRESS' } });
}

export async function getCurrentTask(worksheetId: string): Promise<WorksheetTask | null> {
  const worksheet = await getWorksheet(worksheetId);
  if (!worksheet.tasks) return null;

  // Retourne la première tâche TODO ou IN_PROGRESS
  const activeTask = worksheet.tasks.find(
    (t) => t.status === 'TODO' || t.status === 'IN_PROGRESS'
  );
  return activeTask || null;
}
